import React, { useState } from 'react'
import { Modal, Button, Form, Alert } from 'react-bootstrap'
import API from 'common/logic/API'
import Utils from 'common/Utils'
import BigSpinner from 'common/components/BigSpinner'

interface Props {
    onHide: () => void
}

interface Result {
    value: string
}

function RealizedGainModal({ onHide }: Props) {
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [result, setResult] = useState<Result | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const fetchSummary = (from?: string, to?: string) => {
        setLoading(true)
        setError(null)
        const params: Record<string, unknown> = {}
        if (from) params.start_date = from
        if (to) params.end_date = to
        API.get('/asset-transactions/realized-gain-summary', params)
            .then((r: any) => {
                const data = r.data as Result
                setResult(data ?? { value: '0' })
                setLoading(false)
            })
            .catch((err: any) => {
                setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load realized gain')
                setLoading(false)
            })
    }

    // Initial load when the modal opens: total across all dates.
    React.useEffect(() => { fetchSummary() }, [])

    const applyRange = (e: React.FormEvent) => {
        e.preventDefault()
        fetchSummary(startDate || undefined, endDate || undefined)
    }

    return (
        <Modal show={true} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Realized gain</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={applyRange}>
                    <div className="d-flex align-items-center mb-3" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                        <Form.Control
                            size="sm"
                            type="date"
                            value={startDate}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
                            style={{ width: '160px' }}
                            title="From date"
                        />
                        <span style={{ color: '#888' }}>→</span>
                        <Form.Control
                            size="sm"
                            type="date"
                            value={endDate}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
                            style={{ width: '160px' }}
                            title="To date"
                        />
                        <Button size="sm" variant="outline-secondary" type="submit">Apply</Button>
                        {(startDate || endDate) && (
                            <Button size="sm" variant="link" onClick={() => {
                                setStartDate('')
                                setEndDate('')
                                fetchSummary()
                            }}>Clear</Button>
                        )}
                    </div>
                </Form>
                {loading && <BigSpinner />}
                {error && <Alert variant="danger">{error}</Alert>}
                {!loading && !error && result && (
                    <p className="mb-0">
                        Total realized gain
                        {startDate || endDate ? ' in the selected range' : ' across all transactions'}
                        {': '}
                        <strong style={{ color: Number(result.value) > 0 ? '#28a745' : Number(result.value) < 0 ? '#dc3545' : undefined }}>
                            {Utils.formatMoney(result.value, true)} €
                        </strong>
                    </p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    )
}

export default RealizedGainModal